import { BitkitRibbon, BitkitRibbonProps } from '@bitrise/bitkit-v2';
import { format, parseISO } from 'date-fns';
import { useContext, useState } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { useMutateUserMetadata } from '@/hooks/api/useUserMetadata';
import { useQueryParam } from '@/hooks/useQueryParam';
import { planSelectorPagePath } from '@/utils/monolithApiRouteService';
import { mergeQueryParam } from '@/utils/queryParams';

import ExtendYourLimitDialog from '../ExtendYourLimitDialog/ExtendYourLimitDialog';

const TimeBasedTrialBanner = () => {
  const { mutate } = useMutateUserMetadata({ transform: false });
  const { account } = useContext(GlobalPropsContext);
  const [isBannerVisible, setBannerVisible] = useState(account?.onTimeBasedTrial);
  const isExtendDialogOpen = useQueryParam('show-extend-limit') === 'true';
  if (!account) {
    return null;
  }
  const { slug, timeBasedTrial } = account;

  const onRibbonClose = (key: string) => {
    mutate({ [key]: true });
    setBannerVisible(false);
  };

  if (!isBannerVisible) {
    return null;
  }

  const openExtendDialog = () => {
    mergeQueryParam('show-extend-limit', 'true');
  };

  const closeExtendDialog = () => {
    mergeQueryParam('show-extend-limit', null);
  };

  const getRibbonProps = (): BitkitRibbonProps | null => {
    if (timeBasedTrial?.showOverFairUsageLimitBanner) {
      return {
        action: {
          label: 'Extend limit',
          onClick: openExtendDialog,
        },
        colorVariant: 'red',
        title: 'Your workspace usage during trial has exceeded our fair usage limit.',
      };
    }

    if (timeBasedTrial?.showTrialEndedBanner) {
      return {
        colorVariant: 'red',
        title: 'Your 30 day free trial has ended',
      };
    }

    if (timeBasedTrial?.showXDaysUntilTrialEndBanner) {
      return {
        action: {
          href: planSelectorPagePath(slug),
          label: 'Select plan',
        },
        children: `You have ${timeBasedTrial?.xDaysUntilTrialEnd} days left on the Pro trial.`,
        colorVariant: 'yellow',
      };
    }

    if (timeBasedTrial?.showUnderTrialBanner) {
      return {
        action: {
          href: planSelectorPagePath(slug),
          label: 'Explore all plans',
        },
        children: `Your free trial of Bitrise Pro will expire on ${format(
          parseISO(timeBasedTrial?.endTime || ''),
          'MMMM d, yyyy',
        )}.`,
        colorVariant: 'blue',
        onDismiss: () => onRibbonClose(`time_based_trial_under_trial_banner_closed_on_${slug}`),
      };
    }

    return null;
  };

  const ribbonProps = getRibbonProps();

  return (
    <>
      {ribbonProps && <BitkitRibbon {...ribbonProps} />}
      <ExtendYourLimitDialog isOpen={isExtendDialogOpen} onClose={closeExtendDialog} />
    </>
  );
};

export default TimeBasedTrialBanner;
